import { useParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import { adminSwal as Swal } from "./adminSwal";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { adminApi } from "../../../api/admin";
import {
  formatarTipoUsuario,
  tipoLabelParaNumero,
} from "../../../api/usuarios";
import "./AdminInstituicaoUsuarios.scss";

export default function AdminInstituicaoUsuarios() {
  const { id: instituicaoId } = useParams();
  const [busca, setBusca] = useState("");
  const [tipo, setTipo] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, pageSize, instituicaoId };
      if (busca) params.busca = busca;
      if (tipo !== "") params.tipo = Number(tipo);
      setData(await adminApi.listarUsuarios(params));
    } catch (err) {
      Swal.fire("Erro", err?.message || "Falha ao listar", "error");
    } finally {
      setLoading(false);
    }
  }, [busca, tipo, page, pageSize, instituicaoId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const onCriar = useCallback(async () => {
    const { value, isConfirmed } = await Swal.fire({
      title: "Criar usuário",
      html: `
        <input id="sw-nome" class="swal2-input" placeholder="Nome" />
        <input id="sw-email" class="swal2-input" placeholder="Login" type="text" />
        <input id="sw-senha" class="swal2-input" placeholder="Senha inicial (mín. 6)" type="text" />
        <select id="sw-tipo" class="swal2-input">
          <option value="1">Aluno</option>
          <option value="2">Responsável</option>
          <option value="3" selected>Coordenador</option>
        </select>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: "Criar",
      cancelButtonText: "Cancelar",
      preConfirm: () => {
        const nome = document.getElementById("sw-nome").value.trim();
        const email = document.getElementById("sw-email").value.trim();
        const senha = document.getElementById("sw-senha").value;
        const t = Number(document.getElementById("sw-tipo").value);
        if (!nome || !email || !senha) {
          Swal.showValidationMessage("Todos os campos são obrigatórios");
          return false;
        }
        if (senha.length < 6) {
          Swal.showValidationMessage("Senha precisa ter ao menos 6 caracteres");
          return false;
        }
        return {
          nome,
          email,
          senhaInicial: senha,
          tipo: t,
          instituicaoId,
          exigirTrocaSenhaNoPrimeiroLogin: false,
        };
      },
    });
    if (!isConfirmed) return;
    try {
      await adminApi.criarUsuario(value);
      await Swal.fire("OK", "Usuário criado com sucesso.", "success");
      carregar();
    } catch (err) {
      Swal.fire("Erro", err?.message || "Falha ao criar", "error");
    }
  }, [carregar, instituicaoId]);

  const onEditar = useCallback(
    async (u) => {
      const tipoAtual = tipoLabelParaNumero(u.tipo) ?? Number(u.tipo) ?? 1;
      const sel = (n) => (tipoAtual === n ? "selected" : "");
      const { value, isConfirmed } = await Swal.fire({
        title: `Editar ${u.nome}`,
        html: `
        <input id="sw-nome" class="swal2-input" placeholder="Nome" value="${u.nome}" />
        <input id="sw-email" class="swal2-input" placeholder="Login" value="${u.email}" />
        ${
          tipoAtual === 4
            ? `<select id="sw-tipo" class="swal2-input" disabled><option value="4" selected>Administrador (fixo)</option></select>`
            : `<select id="sw-tipo" class="swal2-input">
                <option value="1" ${sel(1)}>Aluno</option>
                <option value="2" ${sel(2)}>Responsável</option>
                <option value="3" ${sel(3)}>Coordenador</option>
              </select>`
        }
      `,
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonText: "Salvar",
        cancelButtonText: "Cancelar",
        preConfirm: () => {
          const payload = {
            nome: document.getElementById("sw-nome").value.trim(),
            email: document.getElementById("sw-email").value.trim(),
          };
          if (tipoAtual !== 4) {
            payload.tipo = Number(document.getElementById("sw-tipo").value);
          }
          return payload;
        },
      });
      if (!isConfirmed) return;
      try {
        await adminApi.editarUsuario(u.id, value);
        Swal.fire("OK", "Usuário atualizado", "success");
        carregar();
      } catch (err) {
        Swal.fire("Erro", err?.message || "Falha ao salvar", "error");
      }
    },
    [carregar],
  );

  const onExcluir = useCallback(
    async (u) => {
      const step1 = await Swal.fire({
        title: `Excluir ${u.nome}?`,
        text: "Essa ação é irreversível. Deseja continuar?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sim, continuar",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#d33",
      });
      if (!step1.isConfirmed) return;

      const step2 = await Swal.fire({
        title: "Confirmação final",
        html: `<p>O usuário <strong>${u.nome}</strong> (${u.email}) e todos os seus vínculos, eventos criados e estados serão excluídos permanentemente.</p><p><strong>Essa ação não pode ser desfeita.</strong></p>`,
        icon: "error",
        showCancelButton: true,
        confirmButtonText: "Excluir definitivamente",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#d33",
      });
      if (!step2.isConfirmed) return;

      try {
        await adminApi.excluirUsuario(u.id);
        Swal.fire("Excluído", "Usuário removido permanentemente.", "success");
        carregar();
      } catch (err) {
        Swal.fire("Erro", err?.message || "Falha ao excluir", "error");
      }
    },
    [carregar],
  );

  const onResetSenha = useCallback(async (u) => {
    const ok = await Swal.fire({
      title: `Resetar senha de ${u.nome}?`,
      text: "Será gerado um link único válido por 1 hora.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Gerar link",
      cancelButtonText: "Cancelar",
    });
    if (!ok.isConfirmed) return;
    try {
      const res = await adminApi.resetSenha(u.id);
      const expira = new Date(res.expiraEm).toLocaleString();
      Swal.fire({
        title: "Link de redefinição gerado",
        html: `
          <p>Compartilhe este link com <b>${u.email}</b>. Expira em <b>${expira}</b>.</p>
          <textarea id="sw-link" class="admin-swal-copy-field" readonly>${res.resetUrl}</textarea>
          <button type="button" id="sw-copy" class="admin-swal-copy-button">Copiar link</button>
        `,
        showConfirmButton: true,
        confirmButtonText: "Fechar",
        didOpen: () => {
          document.getElementById("sw-copy").onclick = () => {
            navigator.clipboard.writeText(res.resetUrl);
            Swal.showValidationMessage("Link copiado!");
            setTimeout(() => Swal.resetValidationMessage(), 1500);
          };
        },
      });
    } catch (err) {
      Swal.fire("Erro", err?.message || "Falha ao gerar link", "error");
    }
  }, []);

  const columns = useMemo(
    () => [
      { accessorKey: "nome", header: "Nome" },
      { accessorKey: "email", header: "Login" },
      {
        accessorKey: "tipo",
        header: "Perfil",
        cell: (info) => (
          <span
            className={`perfil-badge perfil-${(formatarTipoUsuario(info.getValue()) || "").toLowerCase()}`}
          >
            {formatarTipoUsuario(info.getValue())}
          </span>
        ),
      },
      {
        id: "acoes",
        header: "Ações",
        cell: ({ row }) => {
          const u = row.original;
          const tipoNum = tipoLabelParaNumero(u.tipo) ?? Number(u.tipo);
          const isAdmin = tipoNum === 4;
          return (
            <div className="acoes">
              <button onClick={() => onEditar(u)}>Editar</button>
              <button onClick={() => onResetSenha(u)}>Reset senha</button>
              {!isAdmin && (
                <button className="danger" onClick={() => onExcluir(u)}>
                  Excluir
                </button>
              )}
            </div>
          );
        },
      },
    ],
    [onEditar, onResetSenha, onExcluir],
  );

  const table = useReactTable({
    data: data.items || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / pageSize));

  return (
    <div className="admin-inst-usuarios">
      <header>
        <h2>Usuários</h2>
        <button className="primary" onClick={onCriar}>
          Novo usuário
        </button>
      </header>

      <section className="filtros">
        <input
          type="search"
          placeholder="Buscar por nome ou login"
          value={busca}
          onChange={(e) => {
            setBusca(e.target.value);
            setPage(1);
          }}
        />
        <select
          value={tipo}
          onChange={(e) => {
            setTipo(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Todos os perfis</option>
          <option value="1">Aluno</option>
          <option value="2">Responsável</option>
          <option value="3">Coordenador</option>
          <option value="4">Administrador</option>
        </select>
      </section>

      {loading ? (
        <p className="loading">Carregando...</p>
      ) : (
        <table className="tabela">
          <thead>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((h) => (
                  <th key={h.id}>
                    {flexRender(h.column.columnDef.header, h.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="vazio">
                  Nenhum usuário encontrado.
                </td>
              </tr>
            )}
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <footer className="paginacao">
        <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
          Anterior
        </button>
        <span>
          Página {page} de {totalPages} • Total: {data.total}
        </span>
        <button
          disabled={page >= totalPages}
          onClick={() => setPage((p) => p + 1)}
        >
          Próxima
        </button>
      </footer>
    </div>
  );
}
